import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type RouterHistory,
} from 'vue-router';
import type { Pinia } from 'pinia';
import { useSessionStore } from './stores/session.ts';
import { useWorkspaceStore } from './stores/workspace.ts';
import { useInboxStore } from './stores/inbox.ts';
import { safeNext } from './navigation.ts';
import { usePageTitle } from './composables/usePageTitle.ts';

declare module 'vue-router' {
  interface RouteMeta {
    /** Page chrome: the signed-out layout, or (default) the app shell. */
    layout?: 'auth' | 'app';
    /** Reachable when signed out. */
    public?: boolean;
    /** Only for signed-out visitors (signed-in users are sent home). */
    guestOnly?: boolean;
    title?: string;
  }
}

const auth = (
  path: string,
  name: string,
  title: string,
  component: RouteRecordRaw['component'],
  guestOnly = false,
) =>
  ({
    path,
    name,
    component,
    meta: { layout: 'auth', public: true, guestOnly, title },
  }) as RouteRecordRaw;

/** Project keys are uppercase, so lowercase segments (like "settings") never collide with them. */
const KEY = ':key([A-Z][A-Z0-9]{1,9})';

export const routes: RouteRecordRaw[] = [
  auth('/login', 'login', 'Log in', () => import('../features/auth/LoginPage.vue'), true),
  auth('/signup', 'signup', 'Sign up', () => import('../features/auth/SignupPage.vue'), true),
  auth(
    '/check-email',
    'check-email',
    'Check your email',
    () => import('../features/auth/CheckEmailPage.vue'),
  ),
  auth(
    '/verify-email',
    'verify-email',
    'Confirm email',
    () => import('../features/auth/VerifyEmailPage.vue'),
  ),
  auth(
    '/forgot-password',
    'forgot-password',
    'Reset password',
    () => import('../features/auth/ForgotPasswordPage.vue'),
  ),
  auth(
    '/reset-password',
    'reset-password',
    'Choose a new password',
    () => import('../features/auth/ResetPasswordPage.vue'),
  ),
  auth('/invite', 'invite', 'Invitation', () => import('../features/auth/InvitePage.vue')),
  auth(
    '/unsubscribe',
    'unsubscribe',
    'Stop emails',
    () => import('../features/auth/UnsubscribePage.vue'),
  ),

  // "/" lands on the current organization (see the guard below).
  { path: '/', name: 'home', component: () => import('../features/errors/NotFoundPage.vue') },
  // "My work" is per organization: /my-work lands on the current one's.
  {
    path: '/my-work',
    name: 'my-work-home',
    component: () => import('../features/errors/NotFoundPage.vue'),
  },
  // The inbox is per organization too.
  {
    path: '/inbox',
    name: 'inbox-home',
    component: () => import('../features/errors/NotFoundPage.vue'),
  },
  {
    path: '/settings/agents',
    name: 'my-agents',
    component: () => import('../features/agents/AgentsPage.vue'),
    meta: { title: 'My agents' },
  },
  {
    path: '/settings/security',
    name: 'security-settings',
    component: () => import('../features/settings/SecuritySettingsPage.vue'),
    meta: { title: 'Security' },
  },
  {
    path: '/settings/notifications',
    name: 'notification-settings',
    component: () => import('../features/settings/NotificationSettingsPage.vue'),
    meta: { title: 'Notification settings' },
  },
  {
    path: '/o/:org',
    name: 'org',
    component: () => import('../features/workspace/OrgHomePage.vue'),
    meta: { title: 'Home' },
  },
  {
    path: '/o/:org/work',
    name: 'multi-work',
    component: () => import('../features/multi/MultiWorkPage.vue'),
    meta: { title: 'Across projects' },
  },
  {
    path: '/o/:org/my-work',
    name: 'my-work',
    component: () => import('../features/multi/MultiWorkPage.vue'),
    props: { mine: true },
    meta: { title: 'My work' },
  },
  {
    path: '/o/:org/inbox',
    name: 'inbox',
    component: () => import('../features/inbox/InboxPage.vue'),
    meta: { title: 'Inbox' },
  },
  {
    path: '/o/:org/settings/agents',
    name: 'org-agents',
    component: () => import('../features/agentContext/OrgAgentsPage.vue'),
    meta: { title: 'Agents' },
  },
  {
    path: '/o/:org/settings/audit-log',
    name: 'org-audit-log',
    component: () => import('../features/orgs/OrgAuditLogPage.vue'),
    meta: { title: 'Audit log' },
  },
  {
    // An organization that requires two-factor, for someone without it.
    path: '/o/:org/two-factor-required',
    name: 'two-factor-required',
    component: () => import('../features/errors/TwoFactorRequiredPage.vue'),
    meta: { title: 'Two-factor sign-in required' },
  },
  {
    // An organization whose owner's plan paused this person's access.
    path: '/o/:org/paused',
    name: 'org-paused',
    component: () => import('../features/errors/AccessPausedPage.vue'),
    meta: { title: 'Access paused' },
  },
  {
    path: '/settings/plan',
    name: 'plan',
    component: () => import('../features/plans/PlanPage.vue'),
    meta: { title: 'Your plan' },
  },
  {
    path: '/o/:org/settings',
    name: 'org-settings',
    component: () => import('../features/orgs/OrgSettingsPage.vue'),
    meta: { title: 'Organization settings' },
  },
  {
    path: `/o/:org/${KEY}`,
    component: () => import('../features/projects/ProjectLayout.vue'),
    meta: { title: 'Project' },
    children: [
      {
        path: '',
        name: 'project',
        redirect: (to) => ({ name: 'project-list', params: to.params }),
      },
      {
        path: 'settings',
        name: 'project-settings',
        component: () => import('../features/projects/ProjectSettingsPage.vue'),
        meta: { title: 'Project settings' },
      },
      {
        path: 'list',
        name: 'project-list',
        component: () => import('../features/list/ListView.vue'),
        meta: { title: 'List' },
      },
      {
        path: 'sprints',
        name: 'project-sprints',
        component: () => import('../features/sprints/SprintsPage.vue'),
        meta: { title: 'Sprints' },
      },
      {
        path: 'epics',
        name: 'project-epics',
        component: () => import('../features/epics/EpicsPage.vue'),
        meta: { title: 'Epics' },
      },
      {
        path: 'reports',
        name: 'project-reports',
        component: () => import('../features/reports/ReportsPage.vue'),
        meta: { title: 'Reports' },
      },
      {
        path: 'agents',
        name: 'project-agents',
        component: () => import('../features/agentContext/ProjectAgentsPage.vue'),
        meta: { title: 'Agents' },
      },
      {
        path: 'sprints/:sprintId([0-9a-f-]{36})',
        name: 'sprint',
        component: () => import('../features/sprints/SprintPage.vue'),
        meta: { title: 'Sprint' },
      },
      {
        path: 'items/:number(\\d+)',
        name: 'item',
        component: () => import('../features/work/ItemPage.vue'),
        meta: { title: 'Item' },
      },
      {
        path: 'decisions',
        name: 'project-decisions',
        component: () => import('../features/decisions/DecisionsPage.vue'),
        meta: { title: 'Decisions' },
      },
      {
        path: 'decisions/:number(\\d+)',
        name: 'decision',
        component: () => import('../features/decisions/DecisionPage.vue'),
        meta: { title: 'Decision' },
      },
      {
        path: 'whiteboards',
        name: 'project-boards',
        component: () => import('../features/boards/BoardsPage.vue'),
        meta: { title: 'Whiteboards' },
      },
      {
        path: 'whiteboards/:boardId([0-9a-f-]{36})',
        name: 'board',
        component: () => import('../features/boards/BoardPage.vue'),
        meta: { title: 'Whiteboard' },
      },
      {
        // The tree, and the open page (optional, so moving between pages keeps the tree).
        path: 'docs/:pageId([0-9a-f-]{36})?',
        name: 'project-docs',
        component: () => import('../features/docs/DocsPage.vue'),
        meta: { title: 'Docs' },
      },
      {
        // A file's page, beside the same tree.
        path: 'docs/files/:fileId([0-9a-f-]{36})',
        name: 'project-doc-file',
        component: () => import('../features/docs/DocsPage.vue'),
        meta: { title: 'Docs' },
      },
      {
        path: ':tab(blueprint)',
        name: 'project-tab',
        component: () => import('../features/projects/ProjectTabPage.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../features/errors/NotFoundPage.vue'),
    meta: { title: 'Not found' },
  },
];

export function createAppRouter(pinia: Pinia, history: RouterHistory = createWebHistory()) {
  const router = createRouter({ history, routes, sensitive: true });

  router.beforeEach(async (to) => {
    const session = useSessionStore(pinia);
    const workspace = useWorkspaceStore(pinia);
    await session.ensureLoaded();

    if (!to.meta.public && !session.isAuthenticated) {
      return { name: 'login', query: to.fullPath === '/' ? {} : { next: to.fullPath } };
    }
    if (to.meta.guestOnly && session.isAuthenticated) return safeNext(to.query['next']);
    if (!session.isAuthenticated) {
      workspace.reset();
      useInboxStore(pinia).reset();
      return true;
    }

    if (!workspace.orgs.length) await workspace.loadOrgs();

    const landings = { home: 'org', 'my-work-home': 'my-work', 'inbox-home': 'inbox' } as const;
    const landing = landings[to.name as keyof typeof landings] as string | undefined;
    if (landing) {
      // Not a paused one (shown, not opened): landing picks another.
      const slug =
        workspace.current && !workspace.current.paused
          ? workspace.current.slug
          : workspace.landingSlug();
      if (!slug) return true;
      return { name: landing, params: { org: slug } };
    }

    const org = typeof to.params['org'] === 'string' ? to.params['org'] : null;
    if (org) {
      if (!workspace.orgs.some((o) => o.slug === org)) await workspace.loadOrgs();
      const membership = workspace.orgs.find((o) => o.slug === org);
      if (!membership) {
        return { name: 'not-found', params: { pathMatch: to.path.slice(1).split('/') } };
      }
      // Paused over the owner's plan: nothing in it opens; say why, and whom to ask.
      if (membership.paused) {
        if (to.name !== 'org-paused') return { name: 'org-paused', params: { org } };
        workspace.show(org);
        return true;
      }
      if (to.name === 'org-paused') return { name: 'org', params: { org } };
      // The server refuses everything in it until two-factor is on: say so, and how.
      if (membership.requireTwoFactor && !session.twoFactor) {
        return to.name === 'two-factor-required'
          ? true
          : { name: 'two-factor-required', params: { org } };
      }
      if (to.name === 'two-factor-required') return { name: 'org', params: { org } };
      if (workspace.currentSlug !== org) await workspace.select(org);
    } else if (!workspace.currentSlug) {
      // Pages outside an organization (settings) keep the sidebar on the last one visited.
      const slug = workspace.landingSlug();
      if (slug) await workspace.select(slug);
    }
    return true;
  });

  const pageTitle = usePageTitle();
  router.afterEach((to) => pageTitle.set(to.meta.title));

  return router;
}
